import { styled, keyframes } from '@mui/material/styles';
import { Box, Snackbar } from '@mui/material';

// Utils
import {
    getBody1BoldStyles,
    getBody2BoldStyles,
    getBodyExtraSmallBoldStyles,
    getBodyExtraSmallNormalStyles,
    getBodySmallBoldStyles,
    getBodySmallNormalStyles,
    getH3BoldStyles
} from '../../../core/utils/GetTypographyStyles/GetTypographyStyles';
// Types
import { TypographyStyleObject } from '../../../core/types/TypographyTypes';

const onloadup = keyframes`
  0% {
    opacity: 0;
    transform: translateY(32px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const ClientManagementWrapper = styled(Box)(({ theme }) => {
    const {
        colours: {
            backgrounds: {
                background
            }
        }
    } = theme;
    return {
        background: background,

        height: '100%',
        padding: '34px 35px',
        animation: `${onloadup} 0.3s cubic-bezier(0.680, -0.550, 0.265, 1.550) both`,
        '@media(max-width: 1200px)': {
            paddingLeft: 110,
        },
        '@media(max-width: 768px)': {
            padding: 30,
        },
        '@media(max-width: 480px)': {
            padding: 20,
        }
    }
});

export const ClientManagementContent = styled(Box)(() => {
    return {
        position: 'relative',
        height: '100%',
        maxWidth: 1110
    }
});

export const ClientManagementHeader = styled(Box)(({ theme }) => {
    const {
        colours: {
            text: {
                textWeak
            }
        }
    } = theme;

    const HeaderBoldText: TypographyStyleObject = getH3BoldStyles(theme);
    const Body2BoldStyles: TypographyStyleObject = getBody2BoldStyles(theme);

    return {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 30,
        '& h1': {
            ...HeaderBoldText,
            color: textWeak,
            '@media(max-width: 480px)': {
                ...Body2BoldStyles,
                color: textWeak
            }
        },
        '& button': {
            width: 190,
            '@media(max-width: 480px)': {
                width: '150px!important'
            }
        }
    }
});

export const ClientManagementTableWrapper = styled(Box)(() => {
    return {
        width: '100%'
    }
});

export const VBANDetailsWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isEvenRowIndex'
})<any>(({ theme, isEvenRowIndex }) => {
    const {
        colours: {
            backgrounds: {
                background,
                backgroundAlt
            }
        }
    } = theme;

    return {
        padding: '0px 24px 20px 24px',
        background: isEvenRowIndex ? background : backgroundAlt
    }
});

export const VBANDetailsHeader = styled(Box)(({ theme }) => {
    const BODY_SMALL_BOLD_TEXT = getBodySmallBoldStyles(theme);

    return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
        marginBottom: '16px',
        '& div': {
            ...BODY_SMALL_BOLD_TEXT
        },
        '& button': {
            border: 'none',
            background: 'transparent',
            cursor: 'pointer'
        },
        '& svg': {
            width: '20px',
            height: '20px'
        }
    }
})

export const VBANDetailsContentWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isEvenRowIndex'
})<any>(({ theme, isEvenRowIndex }) => {
    const {
        colours: {
            backgrounds: {
                background,
                backgroundAlt
            }
        }
    } = theme;

    return {
        display: 'flex',
        padding: '20px 24px',
        alignItems: 'flex-start',
        gap: '16px',
        background: isEvenRowIndex ? backgroundAlt : background,
        justifyContent: 'start',
        flexWrap: 'wrap',
        borderRadius: '4px'
    }
});

export const VBANDetailsContent = styled(Box)(({ theme }) => {
    const {
        colours: {
            text: {
                textWeak
            }
        }
    } = theme;
    const BODY_EXTRA_SMALL_NORMAL_TEXT = getBodyExtraSmallNormalStyles(theme);

    return {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 1,
        minWidth: '155px',
        '& > div': {
            ...BODY_EXTRA_SMALL_NORMAL_TEXT,
            color: textWeak
        }
    }
});

export const VBANCodeCopy = styled(Box)(({ theme }) => {
    const BODY_SMALL_NORMAL_TEXT = getBodySmallNormalStyles(theme);

    return {
        display: 'flex',
        position: 'relative',
        alignItems: 'center',
        gap: '4px',
        alignSelf: 'stretch',
        '& > div': {
            ...BODY_SMALL_NORMAL_TEXT,
            '& > svg': {
                width: '12px',
                height: '12px',
                cursor: 'pointer',
                marginLeft: '8px',
                marginBottom: '2px'
            }
        }
    }
})

export const IconSnackbarWrapper = styled(Box)(() => {
    return {
        display: 'flex',
        alignItems: 'center',
        position: 'relative'
    }
})

export const StyledSnackbar = styled(Snackbar)(({ theme }) => {
    const {
        colours: {
            text: {
                textInverse
            }
        }
    } = theme;
    const BODY_EXTRA_SMALL_NORMAL_TEXT = getBodyExtraSmallBoldStyles(theme);

    return {
        position: 'absolute',
        right: '-70px!important',
        bottom: 'auto!important',
        '& > div': {
            display: 'flex!important',
            alignItems: 'center',
        },
        '& div > div': {
            background: '#2D91E9',
            padding: '8px',
            borderRadius: '4px',
            boxShadow: '0 5px 12px 0 rgba(0,0,0,0.08)',
            '& span': {
                ...BODY_EXTRA_SMALL_NORMAL_TEXT,
                color: textInverse
            }
        }
    }
})

export const LoadingSpinnerContainer = styled(Box)(() => {
    return {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
});
